import React, { useState, useEffect, useCallback } from 'react';
import { collection, addDoc, getDocs, deleteDoc, doc, updateDoc, query, where } from 'firebase/firestore';
import {
  getSupplierSessionRemaining,
  formatSupplierBalanceLabel
} from '../../utils/supplierSessionBalanceUtils';

// Para birimi sembol ve formatter yardımcıları
const CURRENCY_SYMBOLS = { TRY: '₺', USD: '$', EUR: '€' };

const fmtCurrency = (v, currency = 'TRY') => {
  const sym = CURRENCY_SYMBOLS[currency] || currency;
  const num = new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v || 0);
  return currency === 'TRY' ? `${num} ₺` : `${sym} ${num}`;
};

const SupplierOrderModal = ({ session, onClose, db, user, onSessionDateChange }) => {
  const currency = session.currency || 'TRY';
  const currencySym = CURRENCY_SYMBOLS[currency] || currency;

  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionData, setSessionData] = useState(session);
  const [sessionDate, setSessionDate] = useState(() => {
    if (session?.sessionDate) return session.sessionDate;
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  });
  const [isSavingSessionDate, setIsSavingSessionDate] = useState(false);
  const [isAddingItem, setIsAddingItem] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const initialFormState = {
    productName: '',
    unitType: 'adet',
    quantity: '',
    unitPrice: '',
    notes: ''
  };
  const [form, setForm] = useState(initialFormState);

  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');

  // Anlık tutar hesabı (form önizlemesi)
  const previewTotal = (parseFloat(form.quantity) || 0) * (parseFloat(form.unitPrice) || 0);

  const fetchSessionDetails = useCallback(async () => {
    try {
      const q = query(collection(db, 'supplier_sessions'), where('__name__', '==', session.id));
      const snap = await getDocs(q);
      if (!snap.empty) {
        setSessionData({ id: snap.docs[0].id, ...snap.docs[0].data() });
      }
    } catch (err) {
      console.error("Session güncel verisi çekilemedi:", err);
    }
  }, [db, session.id]);

  const fetchOrders = useCallback(async () => {
    setIsLoading(true);
    try {
      const q = query(
        collection(db, 'supplier_orders'),
        where('sessionId', '==', session.id)
      );
      const snapshot = await getDocs(q);
      const ordersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      ordersData.sort((a, b) => {
        const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(0);
        const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(0);
        return dateB.getTime() - dateA.getTime();
      });

      setOrders(ordersData);
    } catch (error) {
      console.error("Tedarikçi siparişleri yüklenirken hata:", error);
    } finally {
      setIsLoading(false);
    }
  }, [db, session.id]);

  useEffect(() => {
    fetchOrders();
    fetchSessionDetails();
  }, [fetchOrders, fetchSessionDetails]);

  useEffect(() => {
    if (sessionData?.sessionDate) {
      setSessionDate(sessionData.sessionDate);
    }
  }, [sessionData?.sessionDate]);

  const handleSessionDateChange = async (newDate) => {
    if (!newDate || newDate === sessionDate) return;
    setSessionDate(newDate);
    setIsSavingSessionDate(true);
    try {
      await updateDoc(doc(db, 'supplier_sessions', session.id), {
        sessionDate: newDate,
        updatedAt: new Date()
      });
      setSessionData(prev => ({ ...prev, sessionDate: newDate }));
      if (onSessionDateChange) {
        onSessionDateChange(session.id, newDate);
      }
    } catch (err) {
      console.error('Alım tarihi kaydedilemedi:', err);
      setSessionDate(sessionData.sessionDate || sessionDate);
    } finally {
      setIsSavingSessionDate(false);
    }
  };

  const syncSessionAmount = async (currentOrders) => {
    const totalOrderAmount = currentOrders.reduce((sum, item) => sum + parseFloat(item.totalPrice || 0), 0);
    const paidAmount = parseFloat(sessionData.paidAmount || 0);
    const remainingAmount = totalOrderAmount - paidAmount;

    try {
      await updateDoc(doc(db, 'supplier_sessions', session.id), {
        totalOrderAmount,
        remainingAmount,
        updatedAt: new Date()
      });

      setSessionData(prev => ({
        ...prev,
        totalOrderAmount,
        remainingAmount
      }));
    } catch (error) {
      console.error("Session tutarı güncellenirken hata:", error);
    }
  };

  const handleSaveItem = async (e) => {
    e.preventDefault();
    if (!form.productName || !form.quantity || !form.unitPrice) return;

    const quantity = parseFloat(form.quantity);
    const unitPrice = parseFloat(form.unitPrice);
    const totalPrice = quantity * unitPrice;

    try {
      setIsLoading(true);
      const itemData = {
        sessionId: session.id,
        productName: form.productName,
        unitType: form.unitType,
        quantity,
        unitPrice,
        totalPrice,
        notes: form.notes,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      if (editingItem) {
        await updateDoc(doc(db, 'supplier_orders', editingItem.id), {
          productName: form.productName,
          unitType: form.unitType,
          quantity,
          unitPrice,
          totalPrice,
          notes: form.notes,
          updatedAt: new Date()
        });

        const updatedOrders = orders.map(o =>
          o.id === editingItem.id ? { ...o, ...itemData, id: editingItem.id } : o
        );
        setOrders(updatedOrders);
        await syncSessionAmount(updatedOrders);
        setEditingItem(null);
      } else {
        const docRef = await addDoc(collection(db, 'supplier_orders'), itemData);
        const newOrders = [{ id: docRef.id, ...itemData }, ...orders];
        setOrders(newOrders);
        await syncSessionAmount(newOrders);
      }

      setForm(initialFormState);
      setIsAddingItem(false);
    } catch (error) {
      console.error("Ürün kaydedilirken hata:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteItem = async (itemId) => {
    try {
      setIsLoading(true);
      await deleteDoc(doc(db, 'supplier_orders', itemId));
      const filtered = orders.filter(o => o.id !== itemId);
      setOrders(filtered);
      await syncSessionAmount(filtered);
    } catch (error) {
      console.error("Ürün silinirken hata:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditClick = (item) => {
    setEditingItem(item);
    setForm({
      productName: item.productName,
      unitType: item.unitType,
      quantity: item.quantity,
      unitPrice: item.unitPrice,
      notes: item.notes || ''
    });
    setIsAddingItem(true);
  };

  const handleAddPayment = async (e) => {
    e.preventDefault();
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) return;

    const amount = parseFloat(paymentAmount);
    const newPaidAmount = parseFloat(sessionData.paidAmount || 0) + amount;
    const newRemaining = parseFloat(sessionData.totalOrderAmount || 0) - newPaidAmount;

    const paymentRecord = {
      amount,
      notes: paymentNotes || 'Ödeme',
      date: new Date().toISOString(),
      createdBy: user?.uid || null
    };

    const history = sessionData.paymentHistory ? [...sessionData.paymentHistory, paymentRecord] : [paymentRecord];

    try {
      setIsLoading(true);
      await updateDoc(doc(db, 'supplier_sessions', session.id), {
        paidAmount: newPaidAmount,
        remainingAmount: newRemaining,
        paymentHistory: history,
        updatedAt: new Date()
      });

      setSessionData(prev => ({
        ...prev,
        paidAmount: newPaidAmount,
        remainingAmount: newRemaining,
        paymentHistory: history
      }));

      setPaymentAmount('');
      setPaymentNotes('');
    } catch (err) {
      console.error("Ödeme kaydedilirken hata:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeletePayment = async (index) => {
    if (!sessionData.paymentHistory) return;
    const history = [...sessionData.paymentHistory];
    const removedPayment = history.splice(index, 1)[0];

    const newPaidAmount = Math.max(0, parseFloat(sessionData.paidAmount || 0) - parseFloat(removedPayment.amount || 0));
    const newRemaining = parseFloat(sessionData.totalOrderAmount || 0) - newPaidAmount;

    try {
      setIsLoading(true);
      await updateDoc(doc(db, 'supplier_sessions', session.id), {
        paidAmount: newPaidAmount,
        remainingAmount: newRemaining,
        paymentHistory: history,
        updatedAt: new Date()
      });

      setSessionData(prev => ({
        ...prev,
        paidAmount: newPaidAmount,
        remainingAmount: newRemaining,
        paymentHistory: history
      }));
    } catch (err) {
      console.error("Ödeme silinirken hata:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const fmt = (v) => fmtCurrency(v, currency);
  const sessionRemaining = getSupplierSessionRemaining(sessionData);
  const balanceInfo = formatSupplierBalanceLabel(sessionRemaining);

  return (
    <div className="order-details-modal-overlay">
      <div className="order-details-modal-content">
        <div className="order-details-modal-header">
          <div>
            <h3>
              <i className="fas fa-truck" style={{ color: '#8b5cf6', marginRight: '10px' }}></i>
              {sessionData.dealerName} — Tedarikçi Alım Detayları
              {currency !== 'TRY' && (
                <span style={{ marginLeft: '10px', fontSize: '0.8rem', background: '#ede9fe', color: '#6d28d9', padding: '2px 10px', borderRadius: '20px', fontWeight: 700 }} title={currency}>
                  {currencySym}
                </span>
              )}
            </h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '10px' }}>
              <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#475569', whiteSpace: 'nowrap' }}>
                <i className="fas fa-calendar-alt" style={{ marginRight: '5px', color: '#8b5cf6' }}></i>
                Alım Tarihi:
              </label>
              <input
                type="date"
                className="session-date-input"
                value={sessionDate}
                onChange={(e) => handleSessionDateChange(e.target.value)}
                disabled={isSavingSessionDate}
              />
            </div>
          </div>
          <button type="button" className="close-modal-btn" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="order-details-modal-body" style={{ padding: '30px', display: 'flex', flexDirection: 'column', gap: '30px' }}>

          {/* Ürün Ekleme Paneli */}
          <div style={{ background: 'white', padding: '20px', borderRadius: '14px', border: '1px solid #e2e8f0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <h4 style={{ margin: 0, fontSize: '1.15rem', color: '#1e293b' }}>
                <i className="fas fa-shopping-basket" style={{ color: '#8b5cf6', marginRight: '6px' }}></i> Ürün Alım Kalemleri
              </h4>
              {!isAddingItem && (
                <button
                  type="button"
                  className="add-session-btn"
                  onClick={() => {
                    setIsAddingItem(true);
                    setForm(initialFormState);
                    setEditingItem(null);
                  }}
                  style={{ background: '#8b5cf6', padding: '8px 16px', fontSize: '0.9rem' }}
                >
                  <i className="fas fa-plus"></i> Ürün Ekle
                </button>
              )}
            </div>

            {isAddingItem && (
              <form onSubmit={handleSaveItem} style={{ background: '#f8fafc', padding: '20px', borderRadius: '10px', marginBottom: '15px', border: '1px solid #cbd5e1' }}>
                <h5 style={{ margin: '0 0 15px 0', fontSize: '1rem', color: '#475569' }}>
                  {editingItem ? 'Ürünü Düzenle' : 'Yeni Ürün Ekle'}
                </h5>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', gap: '12px', marginBottom: '15px' }}>
                  <div className="form-group">
                    <label>Ürün Adı *</label>
                    <input
                      type="text"
                      value={form.productName}
                      onChange={(e) => setForm({ ...form, productName: e.target.value })}
                      placeholder="Örn: Tül Kumaş, Stor Mekanizması"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Birim</label>
                    <select
                      value={form.unitType}
                      onChange={(e) => setForm({ ...form, unitType: e.target.value })}
                      style={{ width: '100%', padding: '14px 16px', border: '2px solid #e2e8f0', borderRadius: '10px', background: '#f8fafc' }}
                    >
                      <option value="adet">Adet</option>
                      <option value="metre">Metre</option>
                      <option value="m2">Metrekare (m²)</option>
                      <option value="kg">Kilogram (kg)</option>
                      <option value="cm">Santimetre (cm)</option>
                      <option value="metre tül">Metre Tül</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Miktar *</label>
                    <input
                      type="number"
                      step="any"
                      value={form.quantity}
                      onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                      placeholder="Miktar girin"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Birim Fiyat ({currencySym}) *</label>
                    <input
                      type="number"
                      step="any"
                      value={form.unitPrice}
                      onChange={(e) => setForm({ ...form, unitPrice: e.target.value })}
                      placeholder="Birim Fiyat"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Tutar ({currencySym})</label>
                    <input
                      type="text"
                      value={previewTotal > 0 ? fmtCurrency(previewTotal, currency) : '-'}
                      readOnly
                      style={{
                        width: '100%',
                        padding: '14px 16px',
                        border: '2px solid #ddd6fe',
                        borderRadius: '10px',
                        background: '#f5f3ff',
                        color: '#6d28d9',
                        fontWeight: 700,
                        cursor: 'default'
                      }}
                    />
                  </div>
                </div>
                <div className="form-group" style={{ marginBottom: '15px' }}>
                  <label>Notlar</label>
                  <input
                    type="text"
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    placeholder="Ekstra açıklama girin..."
                  />
                </div>
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    className="cancel-btn"
                    onClick={() => {
                      setIsAddingItem(false);
                      setForm(initialFormState);
                      setEditingItem(null);
                    }}
                    style={{ padding: '8px 20px', fontSize: '0.95rem' }}
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    className="save-btn"
                    style={{ background: '#8b5cf6', padding: '8px 20px', fontSize: '0.95rem' }}
                  >
                    <i className="fas fa-save"></i> {editingItem ? 'Güncelle' : 'Kaydet'}
                  </button>
                </div>
              </form>
            )}

            {orders.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '30px', color: '#94a3b8' }}>
                <i className="fas fa-box-open" style={{ fontSize: '2rem', marginBottom: '8px', display: 'block' }}></i>
                Bu alım listesinde henüz ürün bulunmuyor.
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="sessions-table" style={{ width: '100%', borderCollapse: 'collapse', marginTop: '10px' }}>
                  <thead>
                    <tr style={{ background: '#f8fafc' }}>
                      <th style={{ padding: '12px' }}>Ürün Adı</th>
                      <th style={{ padding: '12px' }}>Birim</th>
                      <th style={{ padding: '12px' }}>Miktar</th>
                      <th style={{ padding: '12px' }}>Birim Fiyat ({currencySym})</th>
                      <th style={{ padding: '12px' }}>Toplam Tutar ({currencySym})</th>
                      <th style={{ padding: '12px' }}>Açıklama</th>
                      <th style={{ padding: '12px', textAlign: 'center' }}>İşlemler</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map(item => (
                      <tr key={item.id} style={{ borderTop: '1px solid #e2e8f0' }}>
                        <td style={{ padding: '12px', fontWeight: 600 }}>{item.productName}</td>
                        <td style={{ padding: '12px', textTransform: 'capitalize' }}>{item.unitType}</td>
                        <td style={{ padding: '12px', fontFamily: 'monospace' }}>{item.quantity}</td>
                        <td style={{ padding: '12px', fontFamily: 'monospace' }}>{fmt(item.unitPrice)}</td>
                        <td style={{ padding: '12px', fontFamily: 'monospace', fontWeight: 700 }}>{fmt(item.totalPrice)}</td>
                        <td style={{ padding: '12px', color: '#64748b', fontSize: '0.9rem' }}>{item.notes || '-'}</td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          <button
                            type="button"
                            onClick={() => handleEditClick(item)}
                            style={{ background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', marginRight: '10px', fontSize: '1rem' }}
                            title="Düzenle"
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteItem(item.id)}
                            style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '1rem' }}
                            title="Sil"
                          >
                            <i className="fas fa-trash-alt"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Finansal Takip ve Ödeme Bölümü */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px' }}>
            {/* Cari Durum Kartı & Ödeme Ekleme */}
            <div style={{ background: 'white', padding: '20px', borderRadius: '14px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <h4 style={{ margin: 0, fontSize: '1.15rem', color: '#1e293b' }}>
                <i className="fas fa-wallet" style={{ color: '#10b981', marginRight: '6px' }}></i> Finansal Durum & Ödeme Girişi
              </h4>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px' }}>
                <div style={{ background: '#f8fafc', padding: '15px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                  <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', marginBottom: '4px' }}>Toplam Alım</div>
                  <div style={{ fontSize: '1.2rem', fontWeight: 800, color: '#1e293b' }}>{fmt(sessionData.totalOrderAmount || 0)}</div>
                </div>
                <div style={{ background: '#f0fdf4', padding: '15px', borderRadius: '10px', border: '1px solid #bbf7d0' }}>
                  <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#166534', textTransform: 'uppercase', marginBottom: '4px' }}>Ödenen</div>
                  <div style={{ fontSize: '1.2rem', fontWeight: 800, color: '#166534' }}>{fmt(sessionData.paidAmount || 0)}</div>
                </div>
                <div style={{
                  background: balanceInfo.type === 'credit' ? '#fff7ed' : '#fef2f2',
                  padding: '15px',
                  borderRadius: '10px',
                  border: `1px solid ${balanceInfo.type === 'credit' ? '#fed7aa' : '#fca5a5'}`
                }}>
                  <div style={{
                    fontSize: '0.78rem',
                    fontWeight: 600,
                    color: balanceInfo.type === 'credit' ? '#c2410c' : '#991b1b',
                    textTransform: 'uppercase',
                    marginBottom: '4px'
                  }}>
                    {balanceInfo.label}
                  </div>
                  <div style={{
                    fontSize: '1.2rem',
                    fontWeight: 800,
                    color: balanceInfo.type === 'credit' ? '#ea580c' : '#dc2626'
                  }}>
                    {fmt(balanceInfo.amount)}
                  </div>
                </div>
              </div>

              <form onSubmit={handleAddPayment} style={{ background: '#f0fdf4', padding: '20px', borderRadius: '10px', border: '1px solid #bbf7d0', marginTop: '10px' }}>
                <h5 style={{ margin: '0 0 15px 0', color: '#166534', fontWeight: 700, fontSize: '0.95rem' }}>
                  <i className="fas fa-hand-holding-usd"></i> Tedarikçiye Yapılan Ödemeyi Kaydet
                </h5>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                  <div className="form-group">
                    <label style={{ color: '#166534' }}>Ödeme Tutarı ({currencySym}) *</label>
                    <input
                      type="number"
                      step="any"
                      value={paymentAmount}
                      onChange={(e) => setPaymentAmount(e.target.value)}
                      placeholder="0.00"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label style={{ color: '#166534' }}>Açıklama / Not</label>
                    <input
                      type="text"
                      value={paymentNotes}
                      onChange={(e) => setPaymentNotes(e.target.value)}
                      placeholder="Örn: Banka havalesi, nakit"
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  className="save-btn"
                  style={{ background: '#10b981', width: '100%', padding: '10px' }}
                >
                  <i className="fas fa-check"></i> Ödemeyi Kaydet
                </button>
              </form>
            </div>

            {/* Ödeme Geçmişi */}
            <div style={{ background: 'white', padding: '20px', borderRadius: '14px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: '15px' }}>
              <h4 style={{ margin: 0, fontSize: '1.15rem', color: '#1e293b' }}>
                <i className="fas fa-history" style={{ color: '#64748b', marginRight: '6px' }}></i> Ödeme Geçmişi
              </h4>

              {!sessionData.paymentHistory || sessionData.paymentHistory.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
                  <i className="fas fa-receipt" style={{ fontSize: '2rem', marginBottom: '8px', display: 'block' }}></i>
                  Kayıtlı ödeme geçmişi bulunmuyor.
                </div>
              ) : (
                <div style={{ overflowY: 'auto', maxHeight: '280px' }}>
                  <table className="sessions-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#f8fafc' }}>
                        <th style={{ padding: '10px' }}>Tarih</th>
                        <th style={{ padding: '10px' }}>Tutar ({currencySym})</th>
                        <th style={{ padding: '10px' }}>Açıklama</th>
                        <th style={{ padding: '10px', textAlign: 'center' }}>İşlem</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sessionData.paymentHistory.map((pm, idx) => {
                        const pmDate = pm.date ? new Date(pm.date) : new Date();
                        return (
                          <tr key={idx} style={{ borderTop: '1px solid #e2e8f0' }}>
                            <td style={{ padding: '10px', fontSize: '0.9rem' }}>
                              {pmDate.toLocaleDateString('tr-TR')} {pmDate.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                            </td>
                            <td style={{ padding: '10px', fontWeight: 700, color: '#16a34a' }}>{fmt(pm.amount)}</td>
                            <td style={{ padding: '10px', color: '#64748b', fontSize: '0.9rem' }}>{pm.notes || '-'}</td>
                            <td style={{ padding: '10px', textAlign: 'center' }}>
                              <button
                                type="button"
                                onClick={() => handleDeletePayment(idx)}
                                style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer' }}
                                title="Ödemeyi Sil"
                              >
                                <i className="fas fa-trash-alt"></i>
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default SupplierOrderModal;
